import type { ReactNode } from 'react';
import { mealTone } from '../lib/meals.js';

/*
 * The pieces the screens share, sized from the Claude Design study.
 *
 *   13px  chips, labels, timestamps — the floor, never a target
 *   14px  metadata under a title
 *   15px  body
 *   16px  buttons
 *   19px  card title
 *   25px  screen heading
 *
 * Controls are 54–56px tall with an 18px radius. A gloved thumb on a factory
 * floor needs the height more than the layout needs it back.
 */

/** Small caps label above a list. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-[13px] md:text-[14.5px] font-extrabold tracking-[0.07em] text-subtle uppercase">{children}</p>
  );
}

type ChipTone = 'neutral' | 'brand' | 'ok' | 'warn' | 'danger';

const CHIP_TONE: Record<ChipTone, string> = {
  neutral: 'bg-neutral-soft text-neutral-ink',
  brand: 'bg-brand text-white',
  ok: 'bg-ok-soft text-ok-ink',
  warn: 'bg-warn-soft text-warn-ink',
  danger: 'bg-danger-soft text-danger-ink',
};

export function Chip({ tone = 'neutral', children }: { tone?: ChipTone; children: ReactNode }) {
  return (
    <span
      className={`rounded-pill px-2.5 py-1 text-[13px] md:text-[14.5px] font-extrabold whitespace-nowrap ${CHIP_TONE[tone]}`}
    >
      {children}
    </span>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`overflow-hidden rounded-card bg-white shadow-[0_4px_14px_rgba(38,64,28,.05)] ${className}`}
    >
      {children}
    </div>
  );
}

type ButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ok' | 'outline' | 'danger';
  disabled?: boolean;
  type?: 'button' | 'submit';
};

const VARIANT: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-brand text-white active:bg-brand-hover',
  ok: 'bg-brand-soft text-brand-ink',
  outline: 'bg-white text-ink',
  danger: 'bg-white text-danger',
};

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled = false,
  type = 'button',
}: ButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`flex h-[56px] w-full items-center justify-center gap-2 rounded-control text-[16px] md:text-[17px] font-extrabold transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${VARIANT[variant]}`}
    >
      {children}
    </button>
  );
}

/** The amber note. It is the one thing an employee can get wrong. */
export function CutoffBanner({ label, detail }: { label: string; detail: string }) {
  return (
    <div className="rounded-tile bg-warn-soft px-4 py-3.5">
      <b className="block text-[15px] md:text-[16.5px] text-warn-deep">{label}</b>
      <span className="text-[13.5px] md:text-[15px] text-warn-ink">{detail}</span>
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-card border border-dashed border-line-dashed px-5 py-10 text-center">
      <b className="block text-[17px] md:text-[18.5px]">{title}</b>
      <p className="mt-1.5 text-[14.5px] md:text-[15.5px] text-muted">{body}</p>
    </div>
  );
}

/**
 * The dish photo, or the space it would occupy.
 *
 * The block stays either way so a card with a picture and one without are the
 * same height — a list that reflows as photos arrive is worse than a list with
 * none. Food is what this app is for, so the photo is the largest thing on a
 * card rather than a thumbnail beside the text.
 */
export function PhotoSlot({
  label,
  src,
  height = 180,
  dimmed = false,
}: {
  label: string;
  src?: string | null;
  height?: number;
  dimmed?: boolean;
}) {
  if (src) {
    return (
      <div style={{ height }} className="relative w-full overflow-hidden bg-panel-soft">
        <img src={src} alt={label} loading="lazy" className="h-full w-full object-cover" />
        {/* A closed meal is greyed rather than removed: taking it away leaves
            somebody wondering where the restaurant went. */}
        {dimmed && <div className="absolute inset-0 bg-white/55" />}
      </div>
    );
  }

  return (
    <div
      style={{ height }}
      className="flex w-full items-center justify-center bg-panel-soft px-3 text-center text-[13px] md:text-[14.5px] leading-[1.3] font-extrabold text-muted"
    >
      {label}
    </div>
  );
}

/** The meal, and when it is. "Today · Lunch" is the whole sentence. */
export function MealChip({ mealType, when }: { mealType: string; when?: string }) {
  const tone = mealTone(mealType);
  const label = mealType.charAt(0).toUpperCase() + mealType.slice(1);

  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-pill px-3 py-[5px] text-[13px] font-extrabold whitespace-nowrap md:text-[14.5px] ${tone.chip}`}
    >
      {when ? `${when} · ${label}` : label}
    </span>
  );
}
